import { NormalizedCacheObject } from '@apollo/client';
import { PostHogProvider } from '@posthog/react/slim';
import { appWithTranslation } from 'next-i18next/pages';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import 'react-phone-number-input/style.css';

import getMetaPixelScript from '@pflegia/analytics/ui/scripts/get-meta-pixel-script';
import { GoogleTagManagerScript } from '@pflegia/ui/application/marketing/components/GoogleTagManagerScript';
import { useUtmToCookie } from '@pflegia/ui/application/marketing/utm/hooks/use-utm-data';
import { AuthProvider, useAuth } from '@pflegia/ui/contexts/auth.context';
import { ConsentBannerProvider } from '@pflegia/ui/contexts/consent-banner.context';
import { CookiesConsentManagerProvider } from '@pflegia/ui/contexts/cookies-consent-manager.context';
import useIsAdvertisingAllowed from '@pflegia/ui/hooks/use-is-advertising-allowed';
import { useRouteTracking } from '@pflegia/ui/hooks/use-route-tracking';
import useScrollRestoration from '@pflegia/ui/hooks/use-scroll-restoration';
import ReCaptchaProvider from '@pflegia/ui/shared/recaptcha-provider/recaptcha-provider';
import ToastProvider from '@pflegia/ui/shared/toaster/ToastProvider';

import Page from '@components/Page';
import Seo from '@components/seo/Seo';

import { WhatsAppOptInProvider } from '@domains/whatsapp-opt-in/WhatsAppOptInContext';

import { Layout } from '@shared/components/layout';
import { APOLLO_CACHE_KEY } from '@shared/graphql/apollo-hydration';
import ApolloWrapper from '@shared/graphql/apollo-wrapper';
import { setupPostHogClient } from '@shared/posthog/get-posthog-client';

import { GlobalSsrProps } from '@utils/global-ssr-props';

import '../globals.css';
import nextI18nextConfig from '../next-i18next.config.mjs';
import DatadogRum from '../shared/components/DatadogRum';
import VercelSpeedInsights from '../shared/components/VercelSpeedInsights';

const MandatoryPhoneNumberModal = dynamic(() => import('@domains/login/components/MandatoryPhoneNumberModal'), {
  ssr: false,
  loading: () => null,
});
const LowPriorityRequiredModal = dynamic(() => import('@domains/login/components/LowPriorityRequiredModal'), {
  ssr: false,
  loading: () => null,
});
const WhatsAppOptInModal = dynamic(() => import('@domains/whatsapp-opt-in/WhatsAppOptInModal'), {
  ssr: false,
  loading: () => null,
});
const PlannedMaintenance = dynamic(() => import('@pflegia/ui/application/components/PlannedMaintenance'), {
  ssr: false,
  loading: () => null,
});

const posthog = setupPostHogClient();

interface PageProps extends GlobalSsrProps {
  [APOLLO_CACHE_KEY]?: NormalizedCacheObject;
}

const MyApp = ({ Component, pageProps }: AppProps<PageProps>): React.JSX.Element => {
  const { authenticated } = pageProps;

  useRouteTracking();
  useUtmToCookie();
  useScrollRestoration();

  return (
    <Layout>
      <Seo />
      <AuthProvider initialAuthenticated={authenticated}>
        <ToastProvider>
          <ApolloWrapper ssrCache={pageProps?.[APOLLO_CACHE_KEY]}>
            <CookiesConsentManagerProvider>
              <ConsentBannerProvider>
                {/* @ts-expect-error PostHog type error with slim bundle */}
                <PostHogProvider client={posthog}>
                  <ReCaptchaProvider>
                    <PageComponent>
                      <Component {...pageProps} />
                    </PageComponent>
                  </ReCaptchaProvider>
                </PostHogProvider>
              </ConsentBannerProvider>
            </CookiesConsentManagerProvider>
            <VercelSpeedInsights />
          </ApolloWrapper>
        </ToastProvider>
      </AuthProvider>
    </Layout>
  );
};

// Having a separate PageComponent so we have access to the Cookies Consent Manager Context to control wether to show/hide the scripts related to the cookies
const PageComponent = ({ children }: { children: React.ReactNode }) => {
  const advertisingAllowed = useIsAdvertisingAllowed();
  const authenticated = useAuth();

  return (
    <Page>
      <Head>
        <meta name='viewport' content='width=device-width, initial-scale=1.0, maximum-scale=5.0, viewport-fit=cover' />

        {process.env.NEXT_PUBLIC_META_PIXEL_ID && advertisingAllowed && (
          <script
            id='metaPixel'
            dangerouslySetInnerHTML={{ __html: getMetaPixelScript(process.env.NEXT_PUBLIC_META_PIXEL_ID) as string }}
          />
        )}
      </Head>
      <GoogleTagManagerScript checkConsent />

      {authenticated && <MandatoryPhoneNumberModal />}
      {authenticated && <LowPriorityRequiredModal />}
      <PlannedMaintenance />
      <WhatsAppOptInProvider>
        {authenticated && <WhatsAppOptInModal />}
        <DatadogRum />
        {children}
      </WhatsAppOptInProvider>
    </Page>
  );
};

export default appWithTranslation(MyApp, nextI18nextConfig);
